import React, { useRef, useState } from 'react';
import { Cropper, CropperRef, CropperPreview } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { Button, Box, Typography } from '@mui/material';

/**
 * Props:
 * - imageSrc: string com o DataURL (ou URL) da imagem selecionada
 * - onCropComplete: (croppedImage: File) => void
 * - onCancel: () => void
 */
interface ImageCropperProps {
  imageSrc: string;
  onCropComplete: (croppedImage: File) => void;
  onCancel: () => void;
}

const ImageCropper: React.FC<ImageCropperProps> = ({
  imageSrc,
  onCropComplete,
  onCancel,
}) => {
  const cropperRef = useRef<CropperRef>(null);

  // Controla quando estamos processando o corte
  const [processing, setProcessing] = useState<boolean>(false);

  /**
   * Manipula o clique no botão "Confirmar Corte".
   */
  const handleCrop = async () => {
    if (!cropperRef.current) return;
    setProcessing(true);

    try {
      // Obtenha o canvas do cropper
      const canvas = cropperRef.current.getCanvas();
      if (!canvas) {
        throw new Error('Canvas não retornado pelo react-advanced-cropper');
      }

      // Converter canvas em Blob
      const blob: Blob | null = await new Promise((resolve) =>
        canvas.toBlob(resolve as BlobCallback, 'image/jpeg', 0.9)
      );

      if (!blob) {
        throw new Error('Falha ao criar Blob a partir do canvas');
      }

      // Converter Blob em File
      const file = new File([blob], 'cropped_image.jpg', {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });

      onCropComplete(file);
    } catch (err) {
      console.error('Erro ao processar o corte:', err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ width: '100%', height: 400, position: 'relative', mb: 2 }}>
        <Cropper
          ref={cropperRef}
          src={imageSrc}
          className="advanced-cropper"
          style={{ width: '100%', height: '100%' }}
          stencilProps={{
            movable: true,
            resizable: true, // importante: permite redimensionar a área
            lines: true,     // mostra a grade de linhas
            handlers: true,  // exibe “handles” nas quinas
          }}
        />
      </Box>

      {/* Pré-visualização menor (opcional) */}
      {/* 
        <CropperPreview
          cropperRef={cropperRef}
          style={{ width: 150, height: 150, border: '1px solid #ccc' }}
        />
      */}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button variant="contained" color="error" onClick={onCancel} disabled={processing}>
          Cancelar
        </Button>
        <Button variant="contained" color="primary" onClick={handleCrop} disabled={processing}>
          {processing ? 'Processando...' : 'Confirmar Corte'}
        </Button>
      </Box>
    </Box>
  );
};

export default ImageCropper;